import type { TFunction } from 'i18next';
import type { Epic, StrategicEpic } from '../../../api/tasks-client/task-client.type';
import type { FilterOption } from '../../../components/filters/filter-panel.type';

/**
 * Gets unique teams from epics based on parent_project
 *
 * @param {Epic[]} epics - Array of epics to get teams from
 * @returns {Array<{ id: string; name: string }>} Array of unique teams with their IDs and names
 */
function getTeamsFromEpics(epics: Epic[]): Array<{ id: string; name: string }> {
  const teamsMap = new Map<string, string>();

  epics.forEach((epic) => {
    const parentProject = (epic as any).parent_project;
    if (parentProject && !teamsMap.has(parentProject.id)) {
      teamsMap.set(parentProject.id, parentProject.name);
    }
  });

  return Array.from(teamsMap.entries()).map(([id, name]) => ({
    id,
    name,
  }));
}

/**
 * Counts epics by team based on parent_project
 *
 * @param {Epic[]} epics - Array of epics to count
 * @param {string} teamId - The ID of the team to count epics for
 * @returns {number} The number of epics by team
 */
function countEpicsByTeam(epics: Epic[], teamId: string): number {
  return epics.filter((epic) => (epic as any).parent_project?.id === teamId).length;
}

/**
 * Returns combined filter options for epic due date filtering
 * Combines due date state options (Has Due Date, No Due Date) with
 * target end date options (Next 90 Days, Last 90 Days)
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {FilterOption[]} Array of combined filter options for due date
 */
function getCombinedDueDateOptions(t: TFunction): FilterOption[] {
  return [
    {
      value: 'has_due_date',
      label: t('strategy.epics.has_due_date'),
    },
    {
      value: 'no_due_date',
      label: t('strategy.epics.no_due_date'),
    },
    {
      value: 'next_90_days',
      label: t('strategy.epics.next_90_days'),
    },
    {
      value: 'last_90_days',
      label: t('strategy.epics.last_90_days'),
    },
  ];
}

/**
 * Returns filter options for epic created date filtering
 * Options: Last 90 Days
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {FilterOption[]} Array of filter options for created date
 */
function getCreatedDateOptions(t: TFunction): FilterOption[] {
  return [
    {
      value: 'last_90_days',
      label: t('strategy.epics.last_90_days'),
    },
  ];
}

/**
 * Returns filter options for epic project filtering based on parent_project
 * Extracts unique parent projects from epics and creates filter options
 *
 * @param {(Epic | StrategicEpic)[]} epics - Array of epics to extract projects from
 * @returns {FilterOption[]} Array of filter options with project values and names
 */
function getProjectOptions(epics: (Epic | StrategicEpic)[]): FilterOption[] {
  const projectsMap = new Map<string, string>();

  epics.forEach((epic) => {
    const parentProject = (epic as any).parent_project;
    if (parentProject && !projectsMap.has(parentProject.id)) {
      projectsMap.set(parentProject.id, parentProject.name);
    }
  });

  return Array.from(projectsMap.entries())
    .map(([id, name]) => ({
      value: id,
      label: name,
    }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

/**
 * Returns filter options for epic completion state filtering
 * Options: Not Started (0%), In Progress (1-99%), Completed (100%)
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {FilterOption[]} Array of filter options for completion states
 */
function getCompletionOptions(t: TFunction): FilterOption[] {
  return [
    {
      value: 'not_started',
      label: t('strategy.epics.not_started'),
    },
    {
      value: 'in_progress',
      label: t('strategy.epics.in_progress'),
    },
    {
      value: 'completed',
      label: t('strategy.epics.completed'),
    },
  ];
}

export {
  countEpicsByTeam,
  getCombinedDueDateOptions,
  getCompletionOptions,
  getCreatedDateOptions,
  getProjectOptions,
  getTeamsFromEpics,
};
